import { Plus } from 'lucide-react'
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import DivisionsList from '../../components/admin/DivisionsList'
import SportsList from '../../components/admin/SportsList'
import Layout from '../../components/Layout'
import { Button } from '../../components/ui/button'
import { useNotification } from '../../contexts/NotificationContext'
import { useUser } from '../../contexts/UserContext'
import {
  createDivision,
  createSport,
  deleteDivision,
  deleteSport,
  fetchDivisions as fetchDivisionsApi,
  fetchSports as fetchSportsApi,
  setSportActive,
  updateDivision,
  updateSport,
} from '../../services/sportService'

export default function SportsManagement({ embedded = false }) {
  const Wrapper = embedded ? Fragment : Layout
  const { user } = useUser()
  const isAdmin = user?.role === 'admin'
  const notify = useNotification()

  const [loading, setLoading] = useState(true)
  const [sports, setSports] = useState([])
  const [searchTerm, setSearchTerm] = useState('')

  const [selectedSportId, setSelectedSportId] = useState(null)
  const selectedSport = useMemo(
    () => sports.find((s) => s.id === selectedSportId) || null,
    [sports, selectedSportId]
  )

  const [divisionsLoading, setDivisionsLoading] = useState(false)
  const [divisions, setDivisions] = useState([])

  const [showAddSportModal, setShowAddSportModal] = useState(false)
  const [showEditSportModal, setShowEditSportModal] = useState(false)
  const [showDeleteSportModal, setShowDeleteSportModal] = useState(false)
  const [editingSport, setEditingSport] = useState(null)
  const [deletingSport, setDeletingSport] = useState(null)

  const [showAddDivisionModal, setShowAddDivisionModal] = useState(false)
  const [showEditDivisionModal, setShowEditDivisionModal] = useState(false)
  const [showDeleteDivisionModal, setShowDeleteDivisionModal] = useState(false)
  const [editingDivision, setEditingDivision] = useState(null)
  const [deletingDivision, setDeletingDivision] = useState(null)

  const [sportForm, setSportForm] = useState({
    name: '',
    description: '',
    is_active: true,
  })

  const [divisionForm, setDivisionForm] = useState({
    name: '',
    description: '',
    gender: 'male',
    team_size: 1,
    is_active: true,
  })

  const fetchSports = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchSportsApi({ searchTerm })

      setSports(data || [])
      if (!selectedSportId && data && data.length > 0) {
        setSelectedSportId(data[0].id)
      }
    } catch (err) {
      console.error('Error fetching sports:', err)
      notify.error('Failed to load sports')
      setSports([])
    } finally {
      setLoading(false)
    }
  }, [searchTerm, selectedSportId])

  const fetchDivisions = useCallback(async () => {
    if (!selectedSportId) {
      setDivisions([])
      return
    }

    setDivisionsLoading(true)
    try {
      const data = await fetchDivisionsApi(selectedSportId)
      setDivisions(data || [])
    } catch (err) {
      console.error('Error fetching divisions:', err)
      notify.error('Failed to load divisions')
      setDivisions([])
    } finally {
      setDivisionsLoading(false)
    }
  }, [selectedSportId])

  useEffect(() => {
    fetchSports()
  }, [fetchSports])

  useEffect(() => {
    fetchDivisions()
  }, [fetchDivisions])

  // Sport handlers
  const handleOpenAddSport = () => {
    setSportForm({ name: '', description: '', is_active: true })
    setShowAddSportModal(true)
  }

  const handleOpenEditSport = (sport) => {
    setEditingSport(sport)
    setSportForm({
      name: sport.name || '',
      description: sport.description || '',
      is_active: !!sport.is_active,
    })
    setShowEditSportModal(true)
  }

  const handleOpenDeleteSport = (sport) => {
    setDeletingSport(sport)
    setShowDeleteSportModal(true)
  }

  const handleSaveSport = async (mode) => {
    if (!sportForm.name) {
      notify.error('Sport name is required')
      return
    }

    try {
      if (mode === 'add') {
        await createSport({
          p_name: sportForm.name,
          p_description: sportForm.description || null,
          p_is_active: sportForm.is_active,
        })
        notify.success(`Sport "${sportForm.name}" created`)
        setShowAddSportModal(false)
      } else {
        await updateSport({
          p_sport_id: editingSport.id,
          p_name: sportForm.name,
          p_description: sportForm.description || null,
          p_is_active: sportForm.is_active,
        })
        notify.success(`Sport "${sportForm.name}" updated`)
        setShowEditSportModal(false)
        setEditingSport(null)
      }

      await fetchSports()
    } catch (err) {
      console.error('Error saving sport:', err)
      notify.error(err?.message || 'Failed to save sport')
    }
  }

  const handleSetSportActive = async (sport, nextIsActive) => {
    try {
      await setSportActive({
        p_sport_id: sport.id,
        p_is_active: nextIsActive,
        p_cascade_to_divisions: true,
      })

      notify.success(`Sport "${sport.name}" ${nextIsActive ? 'enabled' : 'disabled'}`)
      await fetchSports()
      await fetchDivisions()
    } catch (err) {
      console.error('Error toggling sport:', err)
      notify.error(err?.message || 'Failed to update sport status')
    }
  }

  const handleConfirmDeleteSport = async () => {
    try {
      await deleteSport({
        p_sport_id: deletingSport.id,
      })

      notify.success(`Sport "${deletingSport.name}" deleted`)
      setShowDeleteSportModal(false)
      setDeletingSport(null)

      if (selectedSportId === deletingSport.id) {
        setSelectedSportId(null)
      }

      await fetchSports()
      await fetchDivisions()
    } catch (err) {
      console.error('Error deleting sport:', err)
      notify.error(err?.message || 'Failed to delete sport')
    }
  }

  // Division handlers
  const handleOpenAddDivision = () => {
    setDivisionForm({ name: '', description: '', gender: 'male', team_size: 1, is_active: true })
    setShowAddDivisionModal(true)
  }

  const handleOpenEditDivision = (division) => {
    setEditingDivision(division)
    setDivisionForm({
      name: division.name || '',
      description: division.description || '',
      gender: division.gender || 'male',
      team_size: division.team_size || 1,
      is_active: !!division.is_active,
    })
    setShowEditDivisionModal(true)
  }

  const handleOpenDeleteDivision = (division) => {
    setDeletingDivision(division)
    setShowDeleteDivisionModal(true)
  }

  const handleSaveDivision = async (mode) => {
    if (!selectedSportId) {
      notify.error('Select a sport first')
      return
    }

    if (!divisionForm.name || !divisionForm.gender || !divisionForm.team_size) {
      notify.error('Division name, gender, and team size are required')
      return
    }

    try {
      if (mode === 'add') {
        await createDivision({
          p_sport_id: selectedSportId,
          p_name: divisionForm.name,
          p_description: divisionForm.description || null,
          p_gender: divisionForm.gender,
          p_team_size: Number(divisionForm.team_size),
          p_is_active: divisionForm.is_active,
        })
        notify.success(`Division "${divisionForm.name}" created`)
        setShowAddDivisionModal(false)
      } else {
        await updateDivision({
          p_division_id: editingDivision.id,
          p_name: divisionForm.name,
          p_description: divisionForm.description || null,
          p_gender: divisionForm.gender,
          p_team_size: Number(divisionForm.team_size),
          p_is_active: divisionForm.is_active,
        })
        notify.success(`Division "${divisionForm.name}" updated`)
        setShowEditDivisionModal(false)
        setEditingDivision(null)
      }

      await fetchDivisions()
    } catch (err) {
      console.error('Error saving division:', err)
      notify.error(err?.message || 'Failed to save division')
    }
  }

  const handleSetDivisionActive = async (division, nextIsActive) => {
    try {
      await updateDivision({
        p_division_id: division.id,
        p_is_active: nextIsActive,
      })

      notify.success(`Division "${division.name}" ${nextIsActive ? 'enabled' : 'disabled'}`)
      await fetchDivisions()
    } catch (err) {
      console.error('Error toggling division:', err)
      notify.error(err?.message || 'Failed to update division status')
    }
  }

  const handleConfirmDeleteDivision = async () => {
    try {
      await deleteDivision({
        p_division_id: deletingDivision.id,
      })

      notify.success(`Division "${deletingDivision.name}" deleted`)
      setShowDeleteDivisionModal(false)
      setDeletingDivision(null)
      await fetchDivisions()
    } catch (err) {
      console.error('Error deleting division:', err)
      notify.error(err?.message || 'Failed to delete division')
    }
  }

  return (
    <Wrapper>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">Sports</h1>
            <p className="text-muted-foreground">Manage sports and their divisions</p>
          </div>
          {isAdmin && (
            <Button onClick={handleOpenAddSport} className="w-full sm:w-auto">
              <Plus className="h-4 w-4 mr-2" />
              Add Sport
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SportsList
            sports={sports}
            loading={loading}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            selectedSportId={selectedSportId}
            onSelectSport={setSelectedSportId}
            isAdmin={isAdmin}
            onOpenAddSport={handleOpenAddSport}
            onOpenEditSport={handleOpenEditSport}
            onOpenDeleteSport={handleOpenDeleteSport}
            onSetSportActive={handleSetSportActive}
            showAddSportModal={showAddSportModal}
            onCloseAddSportModal={() => setShowAddSportModal(false)}
            sportForm={sportForm}
            onSportFormChange={setSportForm}
            onSaveSport={handleSaveSport}
            showEditSportModal={showEditSportModal}
            onCloseEditSportModal={() => setShowEditSportModal(false)}
            editingSport={editingSport}
            showDeleteSportModal={showDeleteSportModal}
            onCloseDeleteSportModal={() => setShowDeleteSportModal(false)}
            deletingSport={deletingSport}
            onConfirmDeleteSport={handleConfirmDeleteSport}
          />

          <DivisionsList
            divisions={divisions}
            divisionsLoading={divisionsLoading}
            selectedSport={selectedSport}
            isAdmin={isAdmin}
            onOpenAddDivision={handleOpenAddDivision}
            onOpenEditDivision={handleOpenEditDivision}
            onOpenDeleteDivision={handleOpenDeleteDivision}
            onSetDivisionActive={handleSetDivisionActive}
            showAddDivisionModal={showAddDivisionModal}
            onCloseAddDivisionModal={() => setShowAddDivisionModal(false)}
            divisionForm={divisionForm}
            onDivisionFormChange={setDivisionForm}
            onSaveDivision={handleSaveDivision}
            showEditDivisionModal={showEditDivisionModal}
            onCloseEditDivisionModal={() => setShowEditDivisionModal(false)}
            editingDivision={editingDivision}
            showDeleteDivisionModal={showDeleteDivisionModal}
            onCloseDeleteDivisionModal={() => setShowDeleteDivisionModal(false)}
            deletingDivision={deletingDivision}
            onConfirmDeleteDivision={handleConfirmDeleteDivision}
          />
        </div>
      </div>
    </Wrapper>
  )
}
